import { useEffect, useState } from "react";
import { inputClass, ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import {
  linkBuilderAssistantSession,
  resolveBuilderAssistantModel,
  resolveBuilderAssistantSerial,
} from "@/lib/manufacturing/builderAssistantClient";
import type {
  BuilderAssistantModelMatch,
  BuilderAssistantSerialMatch,
  BuilderAssistantSession,
} from "@/lib/manufacturing/builderAssistantTypes";

export function BuilderAssistantLink({
  session,
  onChanged,
}: {
  session: BuilderAssistantSession;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [serial, setSerial] = useState(session.serial_text);
  const [hint, setHint] = useState(session.model_hint ?? "");
  const [matches, setMatches] = useState<BuilderAssistantSerialMatch[]>([]);
  const [models, setModels] = useState<BuilderAssistantModelMatch[]>([]);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setSerial(session.serial_text);
    setHint(session.model_hint ?? "");
  }, [session.id, session.serial_text, session.model_hint]);

  async function saveDetails() {
    setBusy(true);
    setError(null);
    const res = await linkBuilderAssistantSession({
      sessionId: session.id,
      serialText: serial,
      modelHint: hint,
    });
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setNote("Saved.");
    onChanged();
  }

  async function lookup() {
    setBusy(true);
    setError(null);
    setNote(null);
    setMatches([]);
    setModels([]);
    if (serial.trim()) {
      const res = await resolveBuilderAssistantSerial(serial);
      if (!res.ok) {
        setBusy(false);
        setError(res.error);
        return;
      }
      setMatches(res.data.matches);
      if (res.data.matches.length === 0) setNote("No unit matched that serial yet.");
    }
    if (hint.trim()) {
      const res = await resolveBuilderAssistantModel(hint);
      if (!res.ok) {
        setBusy(false);
        setError(res.error);
        return;
      }
      setModels(res.data.matches);
    }
    setBusy(false);
  }

  async function useUnit(match: BuilderAssistantSerialMatch) {
    setBusy(true);
    setError(null);
    const res = await linkBuilderAssistantSession({
      sessionId: session.id,
      unitId: match.unit_id,
      serialText: serial,
      modelHint: hint,
    });
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setNote(null);
    onChanged();
  }

  async function useModel(model: BuilderAssistantModelMatch) {
    setBusy(true);
    setError(null);
    const res = await linkBuilderAssistantSession({
      sessionId: session.id,
      recipeId: model.recipe_id,
      serialText: serial,
      modelHint: model.name,
    });
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setHint(model.name);
    onChanged();
  }

  return (
    <section className="space-y-2 rounded-lg border border-dashed border-gray-300 p-3 dark:border-zinc-600">
      <button type="button" className="text-sm font-medium text-gray-900 dark:text-zinc-100" onClick={() => setOpen((v) => !v)}>
        {open
          ? "Hide serial and model"
          : `Serial ${session.serial_text.trim() || "not set"}${session.model_hint?.trim() ? ` · ${session.model_hint.trim()}` : ""}`}
      </button>
      {open ? (
        <div className="space-y-2">
          <p className="text-sm text-gray-600 dark:text-zinc-400">
            {session.mfg_unit_id
              ? "This inspection is linked to a unit. You can point it at a different one."
              : "Inventory does not have to match before you record the condition. Save what you know, then link a unit when it exists."}
          </p>
          <label className="block text-xs font-medium text-gray-700 dark:text-zinc-300">
            Serial
            <input className={`${inputClass} mt-1 w-full`} value={serial} onChange={(e) => setSerial(e.target.value)} />
          </label>
          <label className="block text-xs font-medium text-gray-700 dark:text-zinc-300">
            Model
            <input className={`${inputClass} mt-1 w-full`} value={hint} onChange={(e) => setHint(e.target.value)} />
          </label>
          <div className="flex flex-wrap gap-2">
            <button type="button" className={ghostBtnClass} disabled={busy} onClick={() => void saveDetails()}>
              Save details
            </button>
            <button type="button" className={ghostBtnClass} disabled={busy} onClick={() => void lookup()}>
              Look up
            </button>
          </div>
          {note ? <p className="text-sm text-gray-700 dark:text-zinc-300">{note}</p> : null}
          {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}
          {matches.map((match) => (
            <div key={match.unit_id} className="flex items-center justify-between gap-2 text-sm">
              <span>Unit {match.serial}</span>
              <button type="button" className={ghostBtnClass} disabled={busy} onClick={() => void useUnit(match)}>
                Link this unit
              </button>
            </div>
          ))}
          {models.map((model) => (
            <div key={model.recipe_id} className="flex items-center justify-between gap-2 text-sm">
              <span>{model.name}</span>
              <button type="button" className={ghostBtnClass} disabled={busy} onClick={() => void useModel(model)}>
                Use this model
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}
